import { Button, Grid, Chip, Icon } from '@material-ui/core';
import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow } from 'components/MyTable';
import PermissionHide from 'components/PermissionHide';
import Spin from 'components/Spin';
import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { toast } from 'react-toastify';
import { useObservable, useCallbackObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import ordersService from 'v2/services/orders';
import { isRequired } from 'validations/defaultMessages';
import * as Yup from 'yup';

import type { IOrder } from 'v2/components/Pages/Orders/interfaces';

import type { IMyTableHeaderIndex } from 'components/MyTable/MyTableHeader';
import type { FormikProps } from 'formik';

const trackingSchema = Yup.object().shape({
  traking_code: Yup.string().required(isRequired('código de rastreio')),
});

const headers: IMyTableHeaderIndex[] = [
  { title: 'Fatura', ordering: false },
  { title: 'Cliente', ordering: false },
  { title: 'Cidade/Estado', ordering: false },
  { title: 'CEP', ordering: false },
  { title: 'Ganho', ordering: false },
  { title: '', ordering: false },
  { title: '', ordering: false },
];

const List = () => {
  const [alreadySent, isAlreadySent] = useState<number[]>([]);
  const [isLoading] = useObservable(() => ordersService.isLoadingOrdersSendToDelivery(), []);

  const [orders] = useObservable(() => {
    return ordersService.getOrdersToSendDeliveryCode();
  }, []);

  const [onSendDeliveryCode] = useCallbackObservable((values: IOrder, form: FormikProps<IOrder>) => {
    return ordersService.sendDeliveryCodeOrder(values).pipe(
      tap(() => {
        try {
          let update = alreadySent;
          update.push(values.invoice_id);
          isAlreadySent(update);
          form.setSubmitting(false);
          toast.success(`Código de rastreio ${values.traking_code} enviado com sucesso`);
        } catch (error) {
          toast.error(error);
        }
      })
    );
  }, []);

  return (
    <MyTable responsive>
      <MyTableHeader index={headers} />
      <MyTableBody>
        {isLoading && (
          <tr>
            <td colSpan={headers.length}>
              <Spin />
            </td>
          </tr>
        )}
        {!isLoading && (!orders || !orders.length) && (
          <tr>
            <td colSpan={headers.length}>Nenhum registro encontrado.</td>
          </tr>
        )}
        {!isLoading &&
          Array.isArray(orders) &&
          orders.map((order: IOrder, index: number) => {
            return (
              <Formik key={`order-${order.invoice_id}`} initialValues={order} validationSchema={trackingSchema} onSubmit={onSendDeliveryCode}>
                {({ isSubmitting, values, dirty, isValid }: FormikProps<IOrder>) => {
                  const sent = alreadySent?.find((v) => v === values.invoice_id);
                  return (
                    <MyTableRow>
                      <MyTableCol screenSizes={{ xs: 4, sm: 4, md: 6, lg: 3 }} mobileOrder={4}>
                        {values.invoice_id}
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={1}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes'>{values.customer.name}</PermissionHide>
                        <PermissionHide inverse={true} action='myeduzz:minhas-entregas:ver-dados-clientes'>
                          <em>oculto</em>
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={5}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes'>
                          {`${values.customer.address.city}/${values.customer.address.state}`}
                        </PermissionHide>
                        <PermissionHide inverse={true} action='myeduzz:minhas-entregas:ver-dados-clientes'>
                          <em>oculto</em>
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }} mobileOrder={6}>
                        <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes'>{values.customer.address.zip_code}</PermissionHide>
                        <PermissionHide inverse={true} action='myeduzz:minhas-entregas:ver-dados-clientes'>
                          <em>oculto</em>
                        </PermissionHide>
                      </MyTableCol>
                      <MyTableCol screenSizes={{ xs: 4, sm: 4, md: 6, lg: 3 }} mobileOrder={7} numeric>
                        <PermissionHide inverse={1} action='myeduzz:minhas-entregas:ocultar-valores'>
                          <FormattedNumber format='BRL' value={values.net_income} />
                        </PermissionHide>
                        <PermissionHide inverse={false} action='myeduzz:minhas-entregas:ocultar-valores'>
                          <em>oculto</em>
                        </PermissionHide>
                      </MyTableCol>

                      <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 4 }}>
                        <Form>
                          <Grid container spacing={2}>
                            <Grid item>
                              <Field
                                placeholder='Código'
                                id={`tracking_code${index}`}
                                name={'traking_code'}
                                fullWidth
                                disabled={sent}
                                value={values.traking_code ? values.traking_code : ''}
                                InputLabelProps={{ shrink: true }}
                                component={TextFormField}
                              />
                            </Grid>
                            <Grid item>
                              {sent ? (
                                <Chip color='secondary' label='Enviado' icon={<Icon style={{ fontSize: 14 }}>done</Icon>} />
                              ) : (
                                <Button
                                  disabled={isSubmitting || !dirty || !isValid}
                                  type='submit'
                                  variant='contained'
                                  style={{ minWidth: 'auto' }}
                                  fullWidth
                                  color='secondary'
                                >
                                  {isSubmitting ? 'Enviando ...' : 'Enviar'}
                                </Button>
                              )}
                            </Grid>
                          </Grid>
                        </Form>
                      </MyTableCol>
                    </MyTableRow>
                  );
                }}
              </Formik>
            );
          })}
      </MyTableBody>
    </MyTable>
  );
};
export default List;
